// frontend/src/components/FollowupsTab.js

import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef, useCallback } from "react";
import { PlusIcon, XMarkIcon, CalendarIcon } from "@heroicons/react/24/outline";

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

const FollowupsTab = forwardRef(({
  onDirtyChange,
  onSummaryChange,
  initialData,
}, ref) => {
  const [followups, setFollowups] = useState([]);
  const isInitialLoadRef = useRef(true);
  const hasDataRef = useRef(false);

  // Load initial data - handle multiple formats
  useEffect(() => {
    console.log('📝 [FOLLOWUPS] Loading initial data:', initialData);
    
    isInitialLoadRef.current = true;
    
    if (!initialData) {
      setFollowups([]);
      isInitialLoadRef.current = false;
      return;
    }

    // If it's already a list (from followupsList or direct array)
    if (Array.isArray(initialData)) {
      console.log('📝 [FOLLOWUPS] Loading from array:', initialData);
      const formattedData = initialData.map(item => ({
        date: item.date || "",
        purpose: item.purpose || "",
        status: item.status || "scheduled",
        notes: item.notes || "",
      }));
      setFollowups(formattedData);
      // Check if there's any actual data
      hasDataRef.current = formattedData.some(f => f.date || f.purpose || f.notes);
      isInitialLoadRef.current = false;
      return;
    }

    // If it has followupsList (old format)
    if (initialData.followupsList && Array.isArray(initialData.followupsList)) {
      console.log('📝 [FOLLOWUPS] Loading from followupsList:', initialData.followupsList);
      const formattedData = initialData.followupsList.map(item => ({
        date: item.date || "",
        purpose: item.purpose || "",
        status: item.status || "scheduled",
        notes: item.notes || "",
      }));
      setFollowups(formattedData);
      hasDataRef.current = formattedData.some(f => f.date || f.purpose || f.notes);
      isInitialLoadRef.current = false;
      return;
    }

    // If it has followUpData array (new format from Plan.js)
    if (initialData.followUpData && Array.isArray(initialData.followUpData)) {
      console.log('📝 [FOLLOWUPS] Loading from followUpData:', initialData.followUpData);
      const formattedData = initialData.followUpData.map(item => ({
        date: item.date || "",
        purpose: item.purpose || "",
        status: item.status || "scheduled",
        notes: item.notes || "",
      }));
      setFollowups(formattedData);
      hasDataRef.current = formattedData.some(f => f.date || f.purpose || f.notes);
      isInitialLoadRef.current = false;
      return;
    }

    // If it's a single object (flat format from backend)
    if (initialData.recheckDate || initialData.notes) {
      console.log('📝 [FOLLOWUPS] Converting from flat object:', initialData);
      const followup = {
        date: initialData.recheckDate || "",
        purpose: initialData.nextVaccinationDate ? "Vaccination" : "Recheck",
        status: "scheduled",
        notes: initialData.notes || "",
      };
      setFollowups([followup]);
      hasDataRef.current = !!(followup.date || followup.purpose || followup.notes);
      isInitialLoadRef.current = false;
      return;
    }

    setFollowups([]);
    hasDataRef.current = false;
    isInitialLoadRef.current = false;
  }, [initialData]);

  // Update summary when followups change
  useEffect(() => {
    const validFollowups = followups.filter(f => f && f.date && f.date.trim() !== '');
    const itemCount = validFollowups.length;
    onSummaryChange?.({ itemCount });
  }, [followups, onSummaryChange]);

  useImperativeHandle(ref, () => ({
    getData: () => {
      const validFollowups = followups.filter(f => f && f.date && f.date.trim() !== '');
      
      return {
        followUpData: validFollowups.map(f => ({
          date: f.date || "",
          purpose: f.purpose || "",
          status: f.status || "scheduled",
          notes: f.notes || "",
        })),
      };
    },
    resetDirty: () => {},
    clearAll: () => {
      setFollowups([]);
      hasDataRef.current = false;
      onDirtyChange?.(true);
    },
  }));

  const addFollowup = () => {
    setFollowups([...followups, { date: "", purpose: "", notes: "", status: "scheduled" }]);
    // Don't mark as dirty - user hasn't entered any data yet
  };

  // Check if a followup has any data
  const hasFollowupData = useCallback((followup) => {
    return followup.date?.trim() || followup.purpose?.trim() || followup.notes?.trim();
  }, []);

  const updateFollowup = (index, field, value) => {
    const updated = [...followups];
    const oldFollowup = { ...updated[index] };
    updated[index] = { ...updated[index], [field]: value };
    setFollowups(updated);
    
    // Only mark as dirty if the new followup has actual data
    const newHasData = hasFollowupData(updated[index]);
    const oldHasData = hasFollowupData(oldFollowup);
    
    // Mark dirty if:
    // 1. The new data has content (even if old didn't)
    // 2. The content changed from something to something else
    if (newHasData || (oldHasData && newHasData !== oldHasData)) {
      onDirtyChange?.(true);
    }
  };

  const removeFollowup = (index) => {
    const followupToRemove = followups[index];
    const hadData = hasFollowupData(followupToRemove);
    setFollowups(followups.filter((_, i) => i !== index));
    
    // Only mark dirty if we're removing a followup that had data
    if (hadData) {
      onDirtyChange?.(true);
    }
  };

  const statusOptions = ["Scheduled", "Completed", "Cancelled", "No-show"];

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <FieldLabel>Follow-ups</FieldLabel>
        <button
          type="button"
          onClick={addFollowup}
          className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-600 hover:text-emerald-700"
        >
          <PlusIcon className="w-4 h-4" /> Add Follow-up
        </button>
      </div>

      {followups.length === 0 ? (
        <button
          type="button"
          onClick={addFollowup}
          className="w-full border-2 border-dashed border-slate-200 rounded-xl py-6 text-center hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
        >
          <div className="flex flex-col items-center gap-1">
            <CalendarIcon className="w-8 h-8 text-slate-300" />
            <p className="text-[12px] text-slate-400">No follow-ups scheduled — click to add</p>
          </div>
        </button>
      ) : (
        <div className="space-y-3">
          {followups.map((followup, idx) => (
            <div key={idx} className="flex flex-col gap-2 p-4 bg-white border border-slate-100 rounded-lg">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] font-bold text-slate-400 min-w-[24px]">
                  #{idx + 1}
                </span>
                <input
                  type="date"
                  value={followup.date || ""}
                  onChange={(e) => updateFollowup(idx, "date", e.target.value)}
                  className="text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent min-w-[150px]"
                />
                <input
                  type="text"
                  value={followup.purpose || ""}
                  onChange={(e) => updateFollowup(idx, "purpose", e.target.value)}
                  placeholder="Purpose of follow-up..."
                  className="flex-1 min-w-[150px] text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                />
                <select
                  value={followup.status || "scheduled"}
                  onChange={(e) => updateFollowup(idx, "status", e.target.value)}
                  className="text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent bg-white min-w-[120px]"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status.toLowerCase()}>{status}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeFollowup(idx)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div>
                <input
                  type="text"
                  value={followup.notes || ""}
                  onChange={(e) => updateFollowup(idx, "notes", e.target.value)}
                  placeholder="Additional notes..."
                  className="w-full text-[12px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

FollowupsTab.displayName = "FollowupsTab";

export default FollowupsTab;